import { Button, IconButton } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { MoreHorizontal, Plus } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/venue/api';
import { Client, Role } from 'apis/restaurant-api/merchant-admin/venue/types';
import { Typography as T } from 'common/typography';

import ChangeUserRightsModal from './ChangeUserRightsModal';
import PasswordCreateModal from './PasswordCreateModal';
import PasswordDeleteModal from './PasswordDeleteModal';
import PasswordResetModal from './PasswordResetModal';
import { stripMerchantAppLoginPrefix } from '../utils';

type Props = {
  venueId: string;
  canCreateDelete: boolean;
};

const Root = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  padding: 0 8px;
`;

const userRoleTranslationRecord: Record<Role, string> = {
  store_manager: 'userManagement.storeManagerRole',
  picker: 'userManagement.pickerRole',
  store_staff_extended: 'userManagement.storeStaffExtended',
};

const AppPasswords = ({ venueId, canCreateDelete }: Props) => {
  const intl = useIntl();

  const [isAddPasswordModalOpen, setIsAddPasswordModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState('');
  const [recordToReset, setRecordToReset] = useState('');
  const [userToChangeRole, setUserToChangeRole] = useState<Client | null>(null);

  const {
    data: logins,
    isLoading,
    refetch,
  } = useQuery(
    [`/v2/merchant-admin/venues/${venueId}/merchant-app-logins`],
    () => api.fetchMerchantAppLogins(venueId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  return (
    <Root>
      <Header>
        <T.Heading6>
          <FormattedMessage id="userManagement.appPasswords" />
        </T.Heading6>
        {canCreateDelete && (
          <Button variant="blue" size="small" icon={<Plus />} onClick={() => setIsAddPasswordModalOpen(true)}>
            <FormattedMessage id="userManagement.addCredentials" />
          </Button>
        )}
      </Header>
      <DataTable
        dataSource={logins?.clients ?? []}
        getRowKey={record => record.login}
        horizontalPadding="wide"
        loading={isLoading}
        columns={[
          {
            id: 'username',
            name: intl.formatMessage({ id: 'userManagement.username' }),
            render: ({ record }) => stripMerchantAppLoginPrefix(record.login),
          },
          {
            id: 'userLevel',
            name: intl.formatMessage({ id: 'userManagement.userLevel' }),
            render: ({ record }) => {
              const messageId = userRoleTranslationRecord[record.role];
              return messageId ? intl.formatMessage({ id: messageId }) : '—';
            },
          },
          {
            id: 'actions',
            name: intl.formatMessage({ id: 'userManagement.actions' }),
            renderName: false,
            alignContent: 'right',
            render: ({ record }) => (
              <LegacyPopover
                showOnHover={false}
                renderArrow={false}
                content={
                  <StyledMenuItemGroup>
                    <MenuActionItem onClick={() => setUserToChangeRole(record)} variant="black">
                      <FormattedMessage id="userManagement.changeRights" />
                    </MenuActionItem>
                    <MenuActionItem onClick={() => setRecordToReset(record.login)} variant="black">
                      <FormattedMessage id="userManagement.resetPassword" />
                    </MenuActionItem>
                    {canCreateDelete && (
                      <MenuActionItem variant="red" onClick={() => setRecordToDelete(record.login)}>
                        <FormattedMessage id="userManagement.removePassword" />
                      </MenuActionItem>
                    )}
                  </StyledMenuItemGroup>
                }
              >
                <IconButton size="small" variant="grey">
                  <MoreHorizontal />
                </IconButton>
              </LegacyPopover>
            ),
          },
        ]}
      />
      <PasswordCreateModal
        isOpen={isAddPasswordModalOpen}
        venueId={venueId}
        onClose={() => setIsAddPasswordModalOpen(false)}
        onDone={refetch}
      />
      <PasswordDeleteModal
        isOpen={!!recordToDelete}
        venueId={venueId}
        username={recordToDelete}
        onClose={() => setRecordToDelete('')}
        onDone={refetch}
      />
      <PasswordResetModal
        isOpen={!!recordToReset}
        onClose={() => setRecordToReset('')}
        venueId={venueId}
        login={recordToReset}
      />
      <ChangeUserRightsModal
        isOpen={!!userToChangeRole}
        user={userToChangeRole}
        onClose={() => setUserToChangeRole(null)}
        venueId={venueId}
        onDone={refetch}
      />
    </Root>
  );
};

export default AppPasswords;
